import { IonCardContent, IonCardTitle, IonCol, IonFabButton, IonGrid, IonIcon, IonRow, IonText } from '@ionic/react';
import { colorPalette } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

/** The Run Kinematics report tab */
import { KinematicsGraphV2 } from '@/pages/ReportV2/components/Graphs';
import styles from '@/pages/ReportV2/Styles/Reports.module.css';

import { KinematicsCardHeader } from '@/components/CardHeader/KinematicsCardHeader';
import { Card } from '@/components/Cards';
import { ChartLegend } from '@/components/ChartLegend/ChartLegend';
import { Icon } from '@/components/Icon';
import { toast } from '@/components/Toaster/Toast';

import { useAuthContext } from '@/contexts/authContext';

import ToggleLeft from '@/assets/icons/toggle-left.svg';

import { RunKinematicData } from '@/models/reportV2.model';

type KinematicsReportProps = {
  tabData: RunKinematicData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const RunKinematicsReportTab = ({ tabData, setFloatingButtons }: KinematicsReportProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const userRole = useAuthContext().state.userState?.role;

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesKinematics', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const seriesPelvis = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides : tabData.pelvisKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesHip = useMemo(() => {
    return showAllStrides ? tabData.hipKinematicData.allStrides : tabData.hipKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesThigh = useMemo(() => {
    return showAllStrides ? tabData.thighKinematicData.allStrides : tabData.thighKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesKnee = useMemo(() => {
    return showAllStrides ? tabData.kneeKinematicData.allStrides : tabData.kneeKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesCalf = useMemo(() => {
    return showAllStrides ? tabData.calfKinematicData.allStrides : tabData.calfKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesAnkle = useMemo(() => {
    return showAllStrides ? tabData.ankleKinematicData.allStrides : tabData.ankleKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesFoot = useMemo(() => {
    return showAllStrides ? tabData.footKinematicData.allStrides : tabData.footKinematicData.mean;
  }, [showAllStrides, tabData]);

  const showLegendToast = () => {
    toast({
      children: <ChartLegend />,
    });
  };

  const showInfoToast = () =>
    toast({
      icon: 'info',
      title: 'Enlarge graphs',
      description: 'Do this by clicking / tapping on the chart',
      duration: 3000,
    });

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={ToggleLeft}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={showLegendToast} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={showInfoToast} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      {/* <IonContent className={mergeClassNames('ion-padding', styles.content)} id='main' color='light'> */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ height: 'fit-content', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <KinematicsCardHeader />
            <IonCardContent>
              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px', backgroundColor: 'transparent' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Pelvis</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.pelvisKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Anterior tilt (-) / Posterior tilt (+)'}
                        title={'Pelvis - Sagittal'}
                        yAxis={seriesPelvis.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.pelvisKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Drop (-) / Hike (+)'}
                        title={'Pelvis - Frontal'}
                        yAxis={seriesPelvis.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.pelvisKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Backward Rotation (-) / Forward Rotation (+)'}
                        title={'Pelvis - Transversal'}
                        yAxis={seriesPelvis.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Hip</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.hipKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Hip - Sagittal'}
                        yAxis={seriesHip.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.hipKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Abduction (-) / Adduction (+)'}
                        title={'Hip - Frontal'}
                        yAxis={seriesHip.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.hipKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Hip - Transversal'}
                        yAxis={seriesHip.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Thigh</IonCardTitle>
                    </IonRow>
                    <IonRow className='ion-no-padding'>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesThigh.Sagittal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.thighKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Extension (-) / Flexion (+)'}
                          title={'Thigh - Sagittal'}
                          yAxis={seriesThigh.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesThigh.Coronal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.thighKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Abduction (-) / Adduction (+)'}
                          title={'Thigh - Frontal'}
                          yAxis={seriesThigh.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesThigh.Transversal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.thighKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Thigh - Transversal'}
                          yAxis={seriesThigh.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Knee</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.kneeKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Knee - Sagittal'}
                        yAxis={seriesKnee.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.kneeKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Valgus (-) / Varus (+)'}
                        title={'Knee - Frontal'}
                        yAxis={seriesKnee.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.kneeKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Knee - Transversal'}
                        yAxis={seriesKnee.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Calf</IonCardTitle>
                    </IonRow>
                    <IonRow className='ion-no-padding'>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesCalf.Sagittal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.calfKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Extension (-) / Flexion (+)'}
                          title={'Calf - Sagittal'}
                          yAxis={seriesCalf.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesCalf.Coronal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.calfKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Abduction (-) / Adduction (+)'}
                          title={'Calf - Frontal'}
                          yAxis={seriesCalf.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesCalf.Transversal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.calfKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Calf - Transversal'}
                          yAxis={seriesCalf.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}

              <IonRow>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Ankle</IonCardTitle>
                  </IonRow>
                  <IonRow>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.ankleKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                        title={'Ankle - Sagittal'}
                        yAxis={seriesAnkle.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.ankleKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Eversion (-) / Inversion (+)'}
                        title={'Ankle - Frontal'}
                        yAxis={seriesAnkle.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        modalData={tabData.ankleKinematicData}
                        parentShowAllStrides={showAllStrides}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Ankle - Transversal'}
                        yAxis={seriesAnkle.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              {userRole === 'ORYX_Admin' && (
                <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                  <IonGrid className='ion-no-padding'>
                    <IonRow className=''>
                      <IonCardTitle className={styles.title}>Foot</IonCardTitle>
                    </IonRow>
                    <IonRow className='ion-no-padding'>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesFoot.Sagittal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.footKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                          title={'Foot - Sagittal'}
                          yAxis={seriesFoot.Sagittal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesFoot.Coronal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.footKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'Eversion (-) / Inversion (+)'}
                          title={'Foot - Frontal'}
                          yAxis={seriesFoot.Coronal.yAxis}
                        />
                      </IonCol>
                      <IonCol className='ion-no-padding' size='4'>
                        <KinematicsGraphV2
                          data={seriesFoot.Transversal.series}
                          graphAnnotations={tabData.graphAnnotations}
                          modalData={tabData.footKinematicData}
                          parentShowAllStrides={showAllStrides}
                          strokeWidth={showAllStrides ? 1 : 2}
                          subtitle={'External Rotation (-) / Internal Rotation (+)'}
                          title={'Foot - Transversal'}
                          yAxis={seriesFoot.Transversal.yAxis}
                        />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRow>
              )}
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
    </>
  );
};
